// Where a newsletter signup came from. Sent to the news app's contacts API as
// `additional_data.source`, which stores it free-form — so the set is kept small
// and shared by the route and its callers, rather than each one inventing a
// literal. Keeps the values groupable in the newsletter admin.
export const NEWSLETTER_SOURCES = {
  footer: 'website footer',
  contactForm: 'contact form',
  subscribeFormBlok: 'subscribe form article blok',
  hearingSurvey: 'hearing survey popup',
  newsletterPage: 'newsletter landing page',
  hearingGuideSave: 'hearing guide save results',
  accountSignup: 'account signup',
} as const

export type NewsletterSource = (typeof NEWSLETTER_SOURCES)[keyof typeof NEWSLETTER_SOURCES]

// The endpoint is public, so an unrecognized source (a scripted POST, a stale
// client) is recorded as unknown rather than trusted verbatim. It is NOT
// defaulted to a real placement: the bug this replaces labelled every signup
// 'website footer', which quietly inflated the footer's numbers.
export const UNKNOWN_NEWSLETTER_SOURCE = 'website'

const KNOWN_SOURCES: readonly string[] = Object.values(NEWSLETTER_SOURCES)

export const resolveNewsletterSource = (value: unknown): string =>
  typeof value === 'string' && KNOWN_SOURCES.includes(value) ? value : UNKNOWN_NEWSLETTER_SOURCE

// The page the form was submitted from, for sources that can appear anywhere
// (the subscribe-form blok). Path only — no origin, no query string, and length
// capped, since it comes from the client.
export const resolveNewsletterPage = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return undefined
  }
  const path = value.split(/[?#]/)[0].trim()
  return path ? path.slice(0, 200) : undefined
}
